import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import React from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { HttpRequest } from 'util/http';
import Button from 'reactstrap/lib/Button';
import './styles.css';
import { userData } from 'util/userData';
import Alert from 'reactstrap/lib/Alert';
import deleteButton from './deleteButton';
import AddNoteModal from "./AddNoteModal";
import CustomTooltip from './CustomTooltip';
import ButtonGroup from "reactstrap/lib/ButtonGroup";

export interface CoachingFormNote {
  id: number;
  survey: string;
  external_data_reference: string;
  client: string;
  username: string;
  created_datetime: string;
  updated_datetime: string;
  message: string;
}

export function CoachingFormNotesGrid() {
  const [notes, setNotes] = React.useState<CoachingFormNote[]>([]);
  const [gettingDataError, setGettingDataError] = React.useState<Error | null>(null);
  const [callingTheApi, setCallingTheApi] = React.useState<string>(null);
  const [apiCallSuccess, setApiCallSuccess] = React.useState<boolean>(true);
  const [addNoteModalOpen, setAddNoteModalOpen] = React.useState<boolean>(false);
  const [editTimeout, setEditTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [searchText, setSearchText] = React.useState<string>('');
  const gridRef = React.useRef<any>();

  const defaultColDefs = {
    editable: true,
    sortable: true,
    filter: true,
    resizable: true,
  }

  const columnDefs: ColDef[] = [
    { field: 'id', editable: false },
    { field: 'survey' },
    { field: 'external_data_reference', headerName: 'External Data Reference' },
    { field: 'client' },
    { field: 'username' },
    {
      field: 'created_datetime',
      headerName: 'Created',
      editable: false,
      valueFormatter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: 'updated_datetime',
      headerName: 'Updated',
      editable: false,
      valueFormatter: (params) => {
        if (params.value) {
          return new Date(params.value).toLocaleString()
        } else {
          return 'N/A'
        }
      }
    },
    {
      field: 'message',
      cellStyle: {
        'white-space': 'normal',
        'line-height': '20px'
      },
      flex: 1,
      minWidth: 200,
      tooltipField: 'message',
      tooltipComponent: 'customTooltip'
    },
    { headerName: 'Delete', cellRenderer: deleteButton }
  ];

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (apiCallSuccess) {
      timeoutId = setTimeout(() => {
        setApiCallSuccess(false);
      }, 2000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [apiCallSuccess]);

  React.useEffect(() => {
    getNotesByClient();
  }, []);

  React.useEffect(() => {
    return () => {
      if (editTimeout) {
        clearTimeout(editTimeout);
      }
    };
  }, [editTimeout]);

  const getNotesByClient = () => {
    const method = 'POST';
    const data = { client: userData.getCurrentProject() };
    setCallingTheApi('Getting all notes...');
    HttpRequest.fetch('/web/v1/coaching_form_notes/get_by_client', { method, data })
      .then((res: any) => {
        if (res.data.notes) {
          setNotes(res.data.notes);
          setCallingTheApi(null);
        } else {
          setGettingDataError(new Error(res.data.error));
          setCallingTheApi(null);
        }
      });
  }

  const deleteRow = (id: number) => {
    const method = 'POST';
    const data = { id };
    setCallingTheApi('Deleting note...');
    setGettingDataError(null);
    HttpRequest.fetch('/web/v1/coaching_form_notes/delete', { method, data })
      .then((res: any) => {
        if (res.data.success) {
          setCallingTheApi(null);
          setApiCallSuccess(true);
          getNotesByClient();
        } else {
          setGettingDataError(new Error(res.data.error));
          setCallingTheApi(null);
        }
      });
  }

  const onCellClicked = (params: any) => {
    if (params.colDef.headerName === 'Delete') {
      deleteRow(params.data.id);
    }
  }

  const onFirstDataRendered = (params: any) => {
    // params.api.sizeColumnsToFit();
    params.columnApi.autoSizeAllColumns();
  }

  const handleOnCellValueChanged = (event: any) => {
    // Clear any existing timeout
    if (editTimeout) {
      clearTimeout(editTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      patchFormNotes(event);
    }, 2000); // 2 second delay

    setEditTimeout(timeout);
  };

  const patchFormNotes = (event: any) => {
    const { data } = event;
    const method = 'POST';
    setCallingTheApi('Saving note changes...');
    setGettingDataError(null);

    HttpRequest.fetch('/web/v1/coaching_form_notes/patch', { method, data })
      .then((res: any) => {
        if (!res.data.error) {
          setCallingTheApi(null);
          setApiCallSuccess(true);
          getNotesByClient();
        } else {
          setGettingDataError(new Error(res.data.error));
          setCallingTheApi(null);
        }
      });
  }

  const addNote = (noteData: Partial<CoachingFormNote>) => {
    const method = 'POST';
    const data = {
      ...noteData,
      client: userData.getCurrentProject(),
    };

    setCallingTheApi('Adding new note...');
    setGettingDataError(null);

    HttpRequest.fetch('/web/v1/coaching_form_notes/add', { method, data })
      .then((res: any) => {
        if (!res.data.error) {
          setNotes([...notes, res.data]);
          setCallingTheApi(null);
          setApiCallSuccess(true);
        } else {
          setGettingDataError(new Error(res.data.error));
          setCallingTheApi(null);
        }
      });
  }

  const handleCloseModal = () => {
    setAddNoteModalOpen(false);
    getNotesByClient();
  }

  const onFilterTextBoxChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    gridRef.current.api.setQuickFilter(e.target.value);
  }, []);

  const onExportClick = () => {
    const params = {
      fileName: `coaching_form_notes_${userData.getCurrentProject()}_${new Date().toISOString().split('T')[0]}.csv`,
      processCellCallback: (params: any) => {
        const value = params.value;
        if (typeof value === 'string') {
          return value.replace(/\r\n|\r|\n/g, ' ');
        }
        return value;
      },
    };

    gridRef.current.api.exportDataAsCsv(params);
  };

  return (
    <>
      <AddNoteModal
        isOpen={addNoteModalOpen}
        toggle={() => handleCloseModal()}
        addNote={addNote}
      />
      <div className='row'>
        <div className="col-4">
          <h1>Coaching Form Notes</h1>
        </div>

        <div className="col-3">
          {gettingDataError &&
            <Alert color='warning'>
              There was an error: {gettingDataError.message}
            </Alert>
          }
          {apiCallSuccess &&
            <Alert color="success">Success!</Alert>
          }
        </div>

        <div className="col-3">
          <input
            type="text"
            value={searchText}
            onChange={onFilterTextBoxChanged}
            placeholder="Search..."
            className="form-control search-input"
          />
        </div>
        <div className="col-2">
          <ButtonGroup>
            <Button onClick={() => setAddNoteModalOpen(true)}>
              Add Note
            </Button>
            <Button onClick={onExportClick}>
              Export
            </Button>
          </ButtonGroup>
        </div>
      </div>
      {callingTheApi &&
        <div className="progress">
          <div className="indeterminate"></div>
        </div>
      }
      <div
        className="ag-theme-balham default-grid-size"
      >
        <AgGridReact
          ref={gridRef}
          rowData={notes}
          columnDefs={columnDefs}
          onFirstDataRendered={onFirstDataRendered}
          defaultColDef={defaultColDefs}
          onCellValueChanged={handleOnCellValueChanged}
          onCellClicked={onCellClicked}
          rowHeight={40}
          frameworkComponents={{
            customTooltip: CustomTooltip
          }}
        />
      </div>
    </>
  );
}

export default CoachingFormNotesGrid;